import React from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { default as dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import errorImg from '../assets/images/error500.png'

dayjs.extend(utc)
/**
 * ErrorHandler component renders a custom error page when an error occurs.
 * It also logs the error to a remote logging service.
 *
 * @module common/ErrorHandler
 * @description A component that renders an error page with error details and a reload button.
 * It also sends error details to a remote server for logging.
 *
 * @component
 * @example
 * return <ErrorHandler error={new Error("Something went wrong!")} />
 *
 * @param {Object} error - The error object to display.
 * @param {string} error.message - The error message.
 * @param {string} [error.label] - Optional label for the error.
 * @param {string} [error.stacktrace] - Optional stack trace for the error.
 *
 * @returns {React.Element} Renders an error page with the error message and a button to reload the page.
 */
export const ErrorHandler = ({ error, status }) => {
  const siteTitle = document.getElementById('siteTitle')
  siteTitle.innerHTML = status ? 'Error - 503' : 'Error - 500'
  try {
    // Log error to remote server
    const customJSON = {
      msg: error.message,
      error: error,
    }

    const isLocalEnvironment =
      process.env.REACT_APP_USE_API_URL_LOCAL === '1' ? '' : process.env.REACT_APP_API_URL_LOCAL

    React.useEffect(() => {
      axios.post(`${isLocalEnvironment}/logger&utcOffset=${dayjs().utcOffset()}`, customJSON)
    }, [customJSON, isLocalEnvironment])

    return (
      <div className="trtui-auth-page-wrapper trtui-py-5 trtui-d-flex trtui-justify-content-center trtui-align-items-center trtui-min-vh-100">
        <div className="trtui-auth-page-content trtui-overflow-hidden trtui-p-0">
          <div className="trtui-container-fluid">
            <div className="trtui-row trtui-justify-content-center">
              <div className="trtui-col-xl-4 trtui-text-center">
                <div className="trtui-error-500 trtui-position-relative">
                  <img
                    src={errorImg}
                    alt
                    className="trtui-img-fluid trtui-error-500-img trtui-error-img"
                  />
                  <h1 className="trtui-title trtui-text-muted">{status || 500}</h1>
                </div>
                <div>
                  <h4>{status ? 'Service Temporarily Unavailable!' : 'Internal Server Error!'}</h4>
                  <p className="trtui-text-muted trtui-w-75 trtui-mx-auto">
                    {status
                      ? 'The service you requested is not available at this time'
                      : 'The Server encountered an internal error or misconfiguration and was unable to complete your request'}
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="trtui-btn trtui-btn-secondary"
                  >
                    {status ? 'Try Again Later' : 'Try Again'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  } catch (err) {
    return (
      <div className="trtui-auth-page-wrapper trtui-py-5 trtui-d-flex trtui-justify-content-center trtui-align-items-center trtui-min-vh-100">
        <div className="trtui-auth-page-content trtui-overflow-hidden trtui-p-0">
          <div className="trtui-container-fluid">
            <div className="trtui-row trtui-justify-content-center">
              <div className="trtui-col-xl-4 trtui-text-center">
                <div className="trtui-error-500 trtui-position-relative">
                  <img
                    src={errorImg}
                    alt
                    className="trtui-img-fluid trtui-error-500-img trtui-error-img"
                  />
                  <h1 className="trtui-title trtui-text-muted">{status || 500}</h1>
                </div>
                <div>
                  <h4>{status ? 'Service Temporarily Unavailable!' : 'Internal Server Error!'}</h4>
                  <p className="trtui-text-muted trtui-w-75 trtui-mx-auto">
                    {status
                      ? 'The service you requested is not available at this time'
                      : 'The Server encountered an internal error or misconfiguration and was unable to complete your request'}
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="trtui-btn trtui-btn-secondary"
                  >
                    {status ? 'Try Again Later' : 'Try Again'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

ErrorHandler.propTypes = {
  /**
   * The error object to display.
   */
  error: PropTypes.shape({
    message: PropTypes.string.isRequired,
    label: PropTypes.string,
    stacktrace: PropTypes.string,
  }).isRequired,
}
